import { css, Global, Theme, useTheme } from '@emotion/react';
import { maxBreakpoints, minBreakpoints } from '@/utils/mediaQuery';
import styled from '@emotion/styled';
import { appDarkGlobalCss } from '@/styles/appDarkMode';

const globalStyles = (theme: Theme) => css`
  :root {
    --max-main-content: 2050px;
  }

  /* 앱 웹뷰 다크 모드 색 (라이트에서는 정의하지 않는다 — styles/appDarkMode.ts) */
  ${appDarkGlobalCss}

  * {
    box-sizing: border-box;
  }

  html,
  body {
    padding: 0;
    margin: 0;
    font-family: var(--font-Pretendard), var(--font-noto-sans), 'Noto Sans KR',
      var(--font-apple-gothic), Apple SD 산돌고딕 Neo, -apple-system,
      BlinkMacSystemFont, sans-serif;
    background: var(--dk-bg, var(--body-color));
    color: var(--dk-text-primary, var(--text-primary-color));
    //overflow-x: hidden;
  }

  body {
    color: var(--text-color);
    line-height: 1.5;
  }

  button {
    border: none;
    font-family: inherit;
    color: var(--dk-text-primary, var(--text-primary-color));
    background-color: transparent;
    cursor: pointer;
  }

  .btn:not(:disabled):not(.disabled) {
    cursor: pointer;
  }

  *:not(.visible-scroll)::-webkit-scrollbar {
    display: none;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  .h1,
  .h2,
  .h3,
  .h4,
  .h5,
  .h6 {
    font-weight: 600;
  }

  @media print {
    #board-print-content {
      @page {
        margin: 25mm 25mm 25mm 25mm;
      }
    }

    /* 파일 뷰어 인쇄 스타일 */
    .file-viewer-print-hide {
      display: none !important;
    }

    .file-viewer-print-content {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: auto !important;
      overflow: visible !important;
      background: var(--dk-bg, white) !important;
    }

    .file-viewer-print-content img {
      max-width: 100% !important;
      height: auto !important;
      transform: none !important;
    }

    .file-viewer-print-content iframe {
      width: 100% !important;
      height: 100vh !important;
      border: none !important;
      box-shadow: none !important;
    }

    .file-viewer-print-content table {
      width: 100% !important;
      transform: none !important;
    }
  }

  @media screen and (${minBreakpoints.md}) {
    html,
    body {
      font-size: 14px;
    }
  }
  @media screen and (${maxBreakpoints.md}) {
    html,
    body {
      font-size: 13px;
    }
  }

  @keyframes typing {
    0% {
      width: 0;
    }
    50% {
      width: 100%;
    }
    100% {
      width: 0;
    }
  }

  @keyframes alarm-shake {
    0% {
      transform: rotate(-15deg);
    }
    4% {
      transform: rotate(15deg);
    }
    8%,
    24% {
      transform: rotate(-18deg);
    }
    12%,
    28% {
      transform: rotate(18deg);
    }
    16% {
      transform: rotate(-22deg);
    }
    20% {
      transform: rotate(22deg);
    }
    32% {
      transform: rotate(-12deg);
    }
    36% {
      transform: rotate(12deg);
    }
    40%,
    100% {
      transform: rotate(0deg);
    }
  }

  .MuiCardMedia-root {
    border: none;
    border-top: 1px solid #ebedf3;
  }

  .icon-help {
    padding: 0.1rem !important;
    color: var(--icon-color-light) !important;
  }
  .icon-right-arrow {
    padding: 0.15rem !important;
    color: var(--text-primary-color) !important;
  }
  .icon-color-light {
    color: var(--icon-color-light) !important;
  }
  input,
  textarea,
  button,
  select,
  a {
    -webkit-tap-highlight-color: transparent;
  }

  *.ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    white-space: nowrap;
  }
  *.ellipsis-line2 {
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    display: -webkit-box !important;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    white-space: normal;
  }
  .ai_border {
    position: relative;
    width: fit-content;
    height: fit-content;
    padding: 6px 10px; /* 원하는 패딩 */
    border: 2px solid transparent;
    border-radius: 8px;
    background-image: linear-gradient(var(--dk-bg, #fff), var(--dk-bg, #fff)),
      linear-gradient(to right, #afb2f4 0%, #e1b3df 100%);
    background-origin: border-box;
    background-clip: padding-box, border-box; /* 수정! */

    &.selected {
      background-image: linear-gradient(var(--dk-purple-soft, #f8eff8), var(--dk-purple-soft, #f8eff8)),
        linear-gradient(to right, #afb2f4 0%, #e1b3df 100%);
    }
  }
  .no-drag {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  // 스토리북에서 사용하는 툴팁을 위해 추가
  .react-aria-Tooltip {
    background-color: var(--dk-bg, white);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.3);
    border-radius: 8px;
  }
`;

const GlobalStyles = () => {
  const theme = useTheme();
  return <Global styles={globalStyles(theme)} />;
};

export const StyledSVGButton = styled.button`
  padding: 0;
  > svg {
    vertical-align: middle;
  }
  opacity: 1;
  :disabled {
    opacity: 0.4;
  }
  :hover {
    opacity: 0.7;
  }
`;

export default GlobalStyles;
